import { useEffect, useRef, useState, type MouseEvent } from 'react'
import { formatDateEt, formatEt } from '@hearingaid/shared'
import { Banner, LoadFailed } from '../components/Banner'
import { Card } from '../components/Card'
import { findOrder, type OrderLookup } from '../lib/data'
import { sendBanner } from '../lib/sendBanner'
import { nextSendCheck } from '../lib/sendWait'
import type { Tab } from '../components/AppBar'

export function OrderDetail({
  orderId,
  submitted,
  onNavigate,
}: {
  orderId: string
  submitted: boolean
  onNavigate: (tab: Tab) => void
}) {
  const [lookup, setLookup] = useState<OrderLookup | null>(null)
  const [waitingForSend, setWaitingForSend] = useState(submitted)
  const [attempt, setAttempt] = useState(0)
  const heading = useRef<HTMLHeadingElement>(null)

  useEffect(() => {
    document.title = 'Hearing aid order'
  }, [])

  useEffect(() => {
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined
    setLookup(null)
    setWaitingForSend(submitted)

    let shown = false
    // Right after a submit the queued email usually has not gone yet: look again a few times
    // (a neutral "Sending the email…" status shows meanwhile) before settling on a banner.
    const load = async (checksDone: number) => {
      const r = await findOrder(orderId)
      if (cancelled) return
      if (shown && r.status !== 'found') {
        // A re-check that fails keeps the order already on screen; the banner settles as it is.
        setWaitingForSend(false)
        return
      }
      shown = true
      setLookup(r)
      const found = r.status === 'found'
      const delay = nextSendCheck(submitted, found, found && r.order.email_sent_at !== null, checksDone)
      if (delay !== null) {
        timer = setTimeout(() => void load(checksDone + 1), delay)
        return
      }
      setWaitingForSend(false)
    }
    void load(0)
    return () => {
      cancelled = true
      if (timer !== undefined) clearTimeout(timer)
    }
  }, [orderId, submitted, attempt])

  const found = lookup?.status === 'found'
  useEffect(() => {
    if (found) heading.current?.focus()
  }, [found])

  const link = (tab: Tab) => (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
    e.preventDefault()
    onNavigate(tab)
  }

  if (lookup === null) return <p className="loading">Loading…</p>

  if (lookup.status === 'failed')
    return <LoadFailed message="Couldn't load the order. Try again." onRetry={() => setAttempt((n) => n + 1)} />

  if (lookup.status === 'notFound')
    return (
      <div className="card">
        <p className="empty" data-icon="search_off">
          Order not found
        </p>
        <p className="link-row">
          <a className="btn text" data-icon="arrow_back" href="/?view=history" onClick={link('history')}>
            Back to order history
          </a>
        </p>
      </div>
    )

  const { order, emailTo } = lookup
  return (
    <>
      {waitingForSend ? (
        <Banner kind="sending" headline="Order submitted.">
          Sending the email to {emailTo}…
        </Banner>
      ) : (
        <SendBannerView banner={sendBanner(order, submitted, emailTo)} />
      )}

      <section className="card">
        <h1 ref={heading} tabIndex={-1}>
          {order.participant_name} – {order.center_name}
        </h1>
        <p className="meta">
          <span data-icon="event">Order date {formatDateEt(order.order_date)}</span>
          <span data-icon="schedule">Submitted {formatEt(order.submitted_at)}</span>
          <span data-icon="person">by {order.submitted_by_name}</span>
        </p>
      </section>

      <Card icon="mail" title="As emailed">
        <p className="subject">
          <strong>Subject:</strong> {order.email_subject}
        </p>
        <pre className="email">{order.email_body}</pre>
      </Card>

      <p className="link-row">
        <a className="btn tonal" data-icon="add" href="/" onClick={link('new')}>
          New order
        </a>
        <a className="btn text" data-icon="history" href="/?view=history" onClick={link('history')}>
          Order history
        </a>
      </p>
    </>
  )
}

function SendBannerView({ banner }: { banner: ReturnType<typeof sendBanner> }) {
  if (banner === null) return null
  return (
    <Banner kind={banner.kind} headline={banner.headline}>
      {banner.text}
    </Banner>
  )
}
